interface PushSubscriptionJSON {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

class PushSubscriptionManager {
  private static instance: PushSubscriptionManager;
  private registration: ServiceWorkerRegistration | null = null;
  private vapidPublicKey: string = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

  private constructor() {}

  public static getInstance(): PushSubscriptionManager {
    if (!PushSubscriptionManager.instance) {
      PushSubscriptionManager.instance = new PushSubscriptionManager();
    }
    return PushSubscriptionManager.instance;
  }

  public async initialize(): Promise<void> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      return;
    }

    try {
      const existingRegistration = await navigator.serviceWorker.getRegistration('/');
      if (existingRegistration) {
        this.registration = existingRegistration;
      } else {
        this.registration = await navigator.serviceWorker.register('/service-worker.js', { scope: '/' });
      }
      await navigator.serviceWorker.ready;
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('[PUSH] Erreur initialisation:', error);
    }
  }

  public async subscribe(userId: string): Promise<PushSubscriptionJSON | null> {
    if (!this.registration) {
      return null;
    }

    try {
      const existingSubscription = await this.registration.pushManager.getSubscription();

      // Réutiliser la subscription existante (évite le blocage Chrome après unsubscribe)
      if (existingSubscription) {
        const subscriptionJSON = existingSubscription.toJSON();
        await this.sendSubscriptionToBackend(userId, subscriptionJSON);
        return subscriptionJSON as PushSubscriptionJSON;
      }

      // Nouvelle subscription
      if (!this.registration.active) {
        await navigator.serviceWorker.ready;
        this.registration = await navigator.serviceWorker.getRegistration('/') || this.registration;
      }

      if (!navigator.serviceWorker.controller) {
        await new Promise<void>((resolve) => {
          const onControllerChange = () => {
            navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
            resolve();
          };
          navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
          setTimeout(resolve, 5000);
        });
      }

      const convertedVapidKey = this.urlBase64ToUint8Array(this.vapidPublicKey);

      if (!this.registration.pushManager) {
        throw new Error('PushManager non disponible');
      }

      const subscribePromise = this.registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: convertedVapidKey
      });

      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Timeout 30s')), 30000);
      });

      const subscription = await Promise.race([subscribePromise, timeoutPromise]);
      const subscriptionJSON = subscription.toJSON();

      await this.sendSubscriptionToBackend(userId, subscriptionJSON);
      return subscriptionJSON as PushSubscriptionJSON;
    } catch (error: any) {
      console.error('[PUSH] Erreur subscription:', error?.message);
      return null;
    }
  }

  public async unsubscribe(): Promise<void> {
    if (!this.registration) return;

    try {
      const subscription = await this.registration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
      }
    } catch (error) {
      console.error('[PUSH] Erreur unsubscribe:', error);
    }
  }

  private async sendSubscriptionToBackend(userId: string, subscription: any): Promise<void> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Token non trouvé');
    }

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        userId: parseInt(userId, 10),
        subscription
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Backend error: ${response.status} - ${errorText}`);
    }
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }
}

export default PushSubscriptionManager.getInstance();
